/**
 * Tabbed Paul conversation viewer — Chat vs executive Summary (BUB-145).
 * Keeps the drawer uncluttered on mobile and desktop.
 */

import React, { useMemo, useState } from "react";
import { Empty, Flex, Tabs, Tag, Typography } from "antd";
import ConversationChatThread from "./ConversationChatThread.jsx";
import ExecutiveSummaryContent from "./ExecutiveSummaryContent.jsx";

const { Text } = Typography;

const TAB_KEYS = {
  CHAT: "chat",
  SUMMARY: "summary",
};

function SummaryMetaRow({ metadata, messageCount, generatedLabel }) {
  const intentHints = Array.isArray(metadata?.intentHints)
    ? metadata.intentHints
    : [];

  const tags = [];
  if (messageCount != null) tags.push({ key: "count", label: `${messageCount} messages` });
  if (generatedLabel) tags.push({ key: "updated", label: `Updated ${generatedLabel}` });
  if (metadata?.inboxStatus) {
    tags.push({ key: "inbox", label: String(metadata.inboxStatus) });
  }
  intentHints.forEach((hint) => tags.push({ key: hint, label: hint, color: "purple" }));

  if (tags.length === 0) return null;

  return (
    <Flex gap={[4, 6]} wrap="wrap">
      {tags.map((tag) => (
        <Tag key={tag.key} color={tag.color}>
          {tag.label}
        </Tag>
      ))}
    </Flex>
  );
}

const ConversationDetailTabs = ({
  messages = [],
  summaryText = null,
  summaryFormat = null,
  metadata = null,
  messageCount = null,
  generatedLabel = null,
  isMobile = false,
  chatMaxHeight = "calc(100vh - 280px)",
  fillHeight = false,
  defaultTab = TAB_KEYS.CHAT,
  summaryExtra = null,
  quoteResponseText = null,
}) => {
  const [activeTab, setActiveTab] = useState(defaultTab);

  const hasChat = messages.length > 0;
  const hasSummary = Boolean(summaryText?.trim());
  const resolvedCount = messageCount ?? messages.length;

  const chatPaneStyle = fillHeight
    ? { width: "100%" }
    : { width: "100%", minHeight: isMobile ? 280 : 320 };

  const summaryPaneStyle = fillHeight
    ? {
        width: "100%",
        paddingBottom: 8,
      }
    : {
        width: "100%",
        maxHeight: chatMaxHeight,
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        paddingBottom: 8,
      };

  const tabItems = useMemo(
    () => [
      {
        key: TAB_KEYS.CHAT,
        label: "Chat",
        children: (
          <Flex vertical style={chatPaneStyle}>
            {hasChat ? (
              <ConversationChatThread
                fullChat={{ messages }}
                maxHeight={chatMaxHeight}
                compact={isMobile}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No messages in this thread yet."
                style={{ padding: isMobile ? "32px 0" : "48px 0" }}
              />
            )}
          </Flex>
        ),
      },
      {
        key: TAB_KEYS.SUMMARY,
        label: "Summary",
        children: (
          <Flex vertical gap={10} style={summaryPaneStyle}>
            {summaryExtra}

            {quoteResponseText && (
              <Text type="secondary" style={{ fontSize: 13 }}>
                Quote response:{" "}
                <Text style={{ fontSize: 13 }}>{String(quoteResponseText)}</Text>
              </Text>
            )}

            <SummaryMetaRow
              metadata={metadata}
              messageCount={resolvedCount}
              generatedLabel={generatedLabel}
            />

            {hasSummary ? (
              <Flex
                vertical
                style={{
                  width: "100%",
                  padding: isMobile ? "12px 14px" : "14px 16px",
                  borderRadius: 10,
                  background: "#fafbfc",
                  border: "1px solid #edf0f3",
                }}
              >
                <Text
                  type="secondary"
                  style={{
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    marginBottom: 6,
                    display: "block",
                  }}
                >
                  Executive summary
                </Text>
                <ExecutiveSummaryContent
                  text={summaryText}
                  format={summaryFormat}
                  isMobile={isMobile}
                />
              </Flex>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No executive summary available yet."
                style={{ padding: isMobile ? "24px 0" : "32px 0" }}
              />
            )}

            {metadata?.quoteId && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Linked quote: {metadata.quoteId}
              </Text>
            )}
          </Flex>
        ),
      },
    ],
    [
      chatMaxHeight,
      chatPaneStyle,
      fillHeight,
      generatedLabel,
      hasChat,
      hasSummary,
      isMobile,
      messages,
      metadata,
      quoteResponseText,
      resolvedCount,
      summaryExtra,
      summaryPaneStyle,
      summaryFormat,
      summaryText,
    ]
  );

  return (
    <Tabs
      activeKey={activeTab}
      onChange={setActiveTab}
      items={tabItems}
      size="small"
      tabBarStyle={{
        marginBottom: 4,
        background: "#fff",
        flexShrink: 0,
      }}
      style={{ width: "100%" }}
    />
  );
};

export default ConversationDetailTabs;
export { TAB_KEYS };
